import { UserRole } from '@/enums/user-role';
import { LoginRequest } from '@/models/login-request';
import { CurrentPerson } from '@/models/person';
import { TokenResponse } from '@/models/token-response';
import { YiviStartSessionResponse } from '@/models/yivi-authentication.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(private httpClient: HttpClient) {}

  login({ username, password }: LoginRequest) {
    return this.httpClient.post<TokenResponse>(`${environment.apiUrl}/api/v1/Authentication/login`, {
      username,
      password,
    });
  }

  refresh(token: string) {
    return this.httpClient.post<TokenResponse>(`${environment.apiUrl}/api/v1/Authentication/refresh`, token);
  }

  startYiviAuthentication() {
    return this.httpClient.post<YiviStartSessionResponse>(
      `${environment.apiUrl}/api/v1/authentication-yivi/start`,
      null,
    );
  }

  currentPerson() {
    return this.httpClient.get<CurrentPerson>(`${environment.apiUrl}/api/v1/CurrentPerson`);
  }

  fetchRolesFromAccessToken(accessToken: string): UserRole[] | null {
    try {
      const decodedToken: Record<string, unknown> = jwtDecode(accessToken);
      const role = decodedToken['role'];

      if (typeof role === 'string') {
        return [role] as UserRole[];
      }

      return role as UserRole[];
    } catch (error) {
      console.warn('Error decoding access token:', error);
      return null;
    }
  }

  fetchNameFromAccessToken(accessToken: string): string | null {
    try {
      const decodedToken: Record<string, unknown> = jwtDecode(accessToken);
      const nameParts = [decodedToken['given_name'], decodedToken['family_name']].filter(
        (part): part is string => typeof part === 'string' && part.length > 0,
      );

      if (nameParts.length > 0) {
        return nameParts.join(' ');
      }

      for (const claim of ['name', 'email']) {
        const value = decodedToken[claim];
        if (typeof value === 'string' && value.length > 0) {
          return value;
        }
      }

      return null;
    } catch (error) {
      console.warn('Error decoding access token:', error);
      return null;
    }
  }

  isTokenExpired(accessToken: string, leeway = 60): boolean {
    try {
      const decodedToken: Record<string, unknown> = jwtDecode(accessToken);

      const expirationTime = Number(decodedToken['exp']);
      if (!Number.isFinite(expirationTime)) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      return expirationTime - currentTime < leeway;
    } catch (error) {
      console.warn('Error decoding access token:', error);
      return true; // Assume expired if there's an error
    }
  }

  forgotPassword(username: string) {
    return this.httpClient.post<boolean>(`${environment.apiUrl}/api/v1/authentication/forgot-password`, { username });
  }

  resetPassword(email: string, resetToken: string, newPassword: string) {
    return this.httpClient.post<{ message: string }>(`${environment.apiUrl}/api/v1/authentication/reset-password`, {
      email,
      resetToken,
      newPassword,
    });
  }
}
